import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import { useBoundaryStateItem } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryStateItem';
import { useBoundaryTerritoryGroupDisplayName } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryTerritoryGroupDisplayName';
import {
  checkBoundaryIsCustom, useBoundaryGroupsSelector,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { boundaryTerritoryGroupsRemoveRequest } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  useActiveBoundaryTerritoryGroupsSelector, useBoundaryTerritoryGroupsSelector,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  activeMapElementsSetActiveBoundary, activeMapElementsSetActiveBoundaryTerritory,
} from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import { boundaryTerritoryGroupHighlightStarted } from '~/store/frontendState/mapTools/boundary/boundarySidebarState/boundarySidebarState.actionCreators';
import { mapSettingsBoundariesClearIsolatedBoundaries } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import { setBoundaryFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import {
  useBoundaryFilters, useBoundaryLocationsFilters,
} from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.selectors';
import { isBoundaryGroupHidden } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.state';
import {
  createMapStatusBarItemForIsolatePolygons, createMapStatusBarItemFromBoundaryTerritoryGroupName,
} from '../item/mapStatusBarItem.factory';
import { type MapStatusBarItem } from '../mapStatusBarItem.type';

export const useActiveBoundariesStatusBarItems = () => {
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();
  const activeBoundaryTerritoryGroups = useActiveBoundaryTerritoryGroupsSelector();
  const boundaryGroups = useBoundaryGroupsSelector();
  const boundaryFilters = useBoundaryFilters();
  const boundaryLocationFilters = useBoundaryLocationsFilters();
  const { getBoundaryTerritoryGroupName } = useBoundaryTerritoryGroupDisplayName();
  const { getBoundaryStateItem } = useBoundaryStateItem();
  const dispatch = useDispatch();
  const [t] = useTranslation();

  const btgBarItems = useMemo<MapStatusBarItem[]>(() => {
    return activeBoundaryTerritoryGroups
      .map(activeBoundaryTerritoryGroup => {
        const boundaryGroup = boundaryGroups.find(boundaryGroup => boundaryGroup.id === activeBoundaryTerritoryGroup.boundaryGroupId);
        const BTGsInGroup = boundaryTerritoryGroups.filter(btg => btg.boundaryGroupId === activeBoundaryTerritoryGroup.boundaryGroupId);
        const groupName = getBoundaryTerritoryGroupName(activeBoundaryTerritoryGroup);

        if (!boundaryGroup) {
          return null;
        }

        const filter = boundaryFilters.get(activeBoundaryTerritoryGroup.boundaryGroupId);
        const isAnyBoundaryVisible = !isBoundaryGroupHidden(filter);

        const showHideActiveBTG = (isVisible: boolean) => {
          dispatch(setBoundaryFilter(activeBoundaryTerritoryGroup.boundaryGroupId, {
            ...filter,
            filteredBoundaryTerritories: [],
            filteredBoundaries: [],
            showAll: isVisible ? TriStateRange.Full : TriStateRange.None,
          }));
        };

        return createMapStatusBarItemFromBoundaryTerritoryGroupName({
          t,
          territoryGroupName: groupName,
          isCustom: checkBoundaryIsCustom(boundaryGroup),
          isVisible: isAnyBoundaryVisible,
          onShowAllChange: showHideActiveBTG,
          onRemove: () => dispatch(boundaryTerritoryGroupsRemoveRequest(BTGsInGroup.map(btg => btg.boundaryTerritoryGroupId))),
          onClick: () => dispatch(boundaryTerritoryGroupHighlightStarted(activeBoundaryTerritoryGroup.boundaryGroupId)),
        });
      })
      .filter((statusBarItem): statusBarItem is MapStatusBarItem => !!statusBarItem);
  },
  [activeBoundaryTerritoryGroups, boundaryGroups, boundaryTerritoryGroups, getBoundaryTerritoryGroupName,
    boundaryFilters, t, dispatch]);

  const isolatedBoundaries = useMemo(() => {
    return boundaryLocationFilters.isolated.entries
      .map(entry => {
        const boundaryGroup = boundaryGroups.find(boundaryGroup => boundaryGroup.id === entry.boundaryGroupId);
        const boundaryTerritoryGroup = boundaryTerritoryGroups.find(btg => btg.boundaryGroupId === entry.boundaryGroupId);

        if (!boundaryGroup || !boundaryTerritoryGroup) {
          return;
        }

        return entry.filter.filteredBoundaries.map((boundaryId) => {
          const boundary = getBoundaryStateItem(boundaryId, entry.boundaryGroupId);
          if (!boundary || !boundaryTerritoryGroup) {
            return;
          }

          return createMapStatusBarItemForIsolatePolygons({
            t, name: boundary.displayName,
            onRemove: () => dispatch(mapSettingsBoundariesClearIsolatedBoundaries()),
            onClick: () => dispatch(activeMapElementsSetActiveBoundary({
              boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
              boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
              boundaryId,
            })),
          });
        }).concat(entry.filter.filteredBoundaryTerritories.map(boundaryTerritoryId => {
          return createMapStatusBarItemForIsolatePolygons({
            t, name: boundaryGroup.name,
            onRemove: () => dispatch(mapSettingsBoundariesClearIsolatedBoundaries()),
            onClick: () => dispatch(activeMapElementsSetActiveBoundaryTerritory({
              boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
              boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
              boundaryTerritoryId,
            })),
          });
        }));
      }).flat().filter(notNullOrUndefined);
  },
  [boundaryGroups, boundaryLocationFilters.isolated.entries, boundaryTerritoryGroups, dispatch, getBoundaryStateItem, t]);

  return useMemo(() => ([
    ...isolatedBoundaries, ...btgBarItems,
  ]), [isolatedBoundaries, btgBarItems]);
};
